import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { useCurrencyRates } from '@/api/currency/queries/useCurrencyRates'
import { Divider } from '@/components/Divider'
import { GrowinWalletSDK } from '@/components/GrowinWalletSDK'
import ClockIcon from '@/components/icons/clock.svg?react'
import CostIcon from '@/components/icons/cost.svg?react'
import FailIcon from '@/components/icons/fail.svg?react'
import PackageIcon from '@/components/icons/package.svg?react'
import SuccessIcon from '@/components/icons/success.svg?react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { getShippingMethodLabel } from '@/helpers/pkgUtils'
import { trackPaymentSuccessful } from '@/lib/analytics'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { RoutePath } from '@/RoutePath'

import { ShippingOptions } from './ShippingPage'

interface FailureResponse {
  status: number
  message: string
}
interface Props {
  shippingOptions: ShippingOptions
  paymentUrl: string
  pkgId: string
  totalPrice: number
}

export default function ProcessPage({ shippingOptions, paymentUrl, pkgId, totalPrice }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: currencyRates } = useCurrencyRates()

  const queryClient = useQueryClient()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const callbackPrice = searchParams.get('callbackPrice')
  const [showSuccessDialog, setShowSuccessDialog] = useState(!!callbackPrice)
  const [paymentError, setPaymentError] = useState<FailureResponse | null>(null)

  const totalILSPrice = totalPrice * 0.9 * 1.03 * (currencyRates?.poalimRate || 0) + totalPrice * 0.1 * (currencyRates?.BOIrate || 0)

  // Calculate delivery date range (14-21 business days from today)
  const calculateDeliveryDates = () => {
    const today = new Date()
    const addBusinessDays = (date: Date, days: number) => {
      let count = 0
      const result = new Date(date)
      while (count < days) {
        result.setDate(result.getDate() + 1)
        const dayOfWeek = result.getDay()
        // Skip weekends (Saturday=6, Sunday=0)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          count++
        }
      }
      return result
    }

    const minDate = addBusinessDays(today, 14)
    const maxDate = addBusinessDays(today, 21)

    return t('payment.deliveryDateRange', {
      minDate,
      maxDate,
    })
  }

  useEffect(() => {
    if (!paymentUrl && !callbackPrice) {
      navigate(`${RoutePath.PaymentSummary}/${pkgId}`)
    }
  }, [pkgId, paymentUrl, callbackPrice, navigate])

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return
    if (!showSuccessDialog || !pkgId) return
    if (!callbackPrice && !currencyRates) return
    const valueFromCallback = callbackPrice ? parseFloat(callbackPrice.replace(',', '.')) : NaN
    const value = Number.isFinite(valueFromCallback) ? valueFromCallback : totalILSPrice
    trackPaymentSuccessful({
      packageId: pkgId,
      value: Number.isFinite(value) ? value : undefined,
    })
  }, [showSuccessDialog, pkgId, callbackPrice, currencyRates, totalILSPrice])

  const onBackToUserPkgs = async () => {
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_PACKAGES] })
    setShowSuccessDialog(false)
    navigate(RoutePath.ClientPkg)
  }
  const onClose = async () => {
    // Don't redirect on close, let user see the error if there is one
    if (!paymentError) {
      navigate(`${RoutePath.PaymentSummary}/${pkgId}`)
    }
  }
  const onError = (error: FailureResponse) => {
    setPaymentError(error)
  }
  const handleBackToSummary = () => {
    setPaymentError(null)
    navigate(`${RoutePath.PaymentSummary}/${pkgId}`)
  }
  return (
    <div className='h-[calc(100dvh-var(--navbar-height)-120px)]'>
      {/* Payment Interface */}
      {!showSuccessDialog && !paymentError && (
        <GrowinWalletSDK authCode={paymentUrl} onSuccess={() => setShowSuccessDialog(true)} onError={onError} onClose={onClose} />
      )}

      {/* Error Dialog */}
      {paymentError && (
        <Card>
          <CardContent className='flex flex-col items-center gap-4'>
            {/* Error Icon */}
            <div className='h-6 w-6'>
              <FailIcon className='h-full w-full text-red-500' />
            </div>

            {/* Title and Description */}
            <div className='flex flex-col items-center gap-4'>
              <h2 className='text-text-primary text-center text-base font-medium'>{t('payment.paymentError')}</h2>
              <p className='text-text-secondary text-center text-sm'>{paymentError.message || t('payment.paymentErrorDefault')}</p>
            </div>

            <Divider />

            {/* Back Button */}
            <Button size='sm' variant='default' onClick={handleBackToSummary} className='w-full max-w-[335px]'>
              {t('payment.backToPayment')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Success Dialog */}
      {showSuccessDialog && (
        <Card>
          <CardContent className='flex flex-col items-center gap-4'>
            {/* Success Icon */}
            <div className='h-6 w-6'>
              <SuccessIcon className='text-icon-primary h-full w-full' />
            </div>

            {/* Title and Description */}
            <div className='flex flex-col items-center gap-4'>
              <h2 className='text-text-primary text-center text-base font-medium'>{t('payment.packageOnTheWay')}</h2>
              <p className='text-text-secondary text-center text-sm'>
                {t('payment.successMessage')}
                <br />
                {t('payment.successMessage2')}
                <br />
                {t('payment.successMessage3')}
              </p>
            </div>

            {/* Info Container */}
            <div className='bg-button-primary-selected-segment w-full rounded-lg p-4'>
              <div className='flex flex-col gap-2'>
                {/* Cost Info */}
                <div className='flex items-center gap-2'>
                  <CostIcon className='text-text-secondary size-4' />
                  <span className='text-text-secondary text-sm'>
                    {t('payment.shippingCostTotal')} {callbackPrice ?? totalILSPrice.toFixed(2)} ₪
                  </span>
                </div>

                {/* Package Count */}
                <div className='flex items-center gap-2'>
                  <PackageIcon className='text-text-secondary size-4' />
                  <span className='text-text-secondary text-sm'>
                    {t('payment.shippingType')}: {getShippingMethodLabel(shippingOptions.selectedShippingMethod, t)}
                  </span>
                </div>

                {/* Time Info */}
                <div className='flex items-center gap-2'>
                  <ClockIcon className='text-text-secondary size-4' />
                  <span className='text-text-secondary text-sm'>
                    {t('payment.deliveryExpected')} {calculateDeliveryDates()}
                  </span>
                </div>
              </div>
            </div>
            <p className='text-text-secondary self-start text-xs'>{t('payment.cancelBeforeShip')}</p>
            <Divider />
            <Link to={RoutePath.ClientPkg}>
              <Button size='sm' variant='ghost' onClick={onBackToUserPkgs} className='text-text-link text-sm'>
                {t('payment.backToPackages')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
